import { useNotifications } from 'src/hooks/query/notification/use-notifications';
import { Can } from 'src/utils/can';
import {
  Actions,
  AppArea,
  NotificationPayload,
  SortingDirection,
  Subjects,
} from 'src/graphql/generated/graphql';
import { Checked } from '@otp/icons';
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { Routes } from 'src/components/common/router/constants';
import { Link } from 'react-router-dom';
import { formatDate } from '@otp/utils';
import { getCategoryIcon } from '../../utils/category-mapper';
import clsx from 'clsx';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import styles from './index.module.css';
import { useDeviceContext } from 'src/context/device.context';
import { parseNotification } from 'src/components/notifications/utils/notification-parser';
import { useAuthContext } from 'src/context/auth.context';

const PAGINATION_LIMIT = 9;

export const NotificationsPopup = ({
  seeAllCallback,
}: {
  seeAllCallback: (event: any) => void;
}) => {
  const { user } = useAuthContext();
  const { t } = useTranslation();
  const { isMobile } = useDeviceContext();
  const { loading, data } = useNotifications({
    filter: {
      isSeen: false,
    },
    sorting: {
      createdAt: SortingDirection.Desc,
    },
    page: 0,
    pageLimit: PAGINATION_LIMIT,
  });

  const records = data?.records || [];
  const title = t('notifications.popup.title');
  return (
    <Can I={Actions.Read} a={Subjects.Notifications} appArea={AppArea.Common}>
      <ContentWrapper
        className={clsx(styles.wrapper, 'p-1', 'gap-1', {
          'max-w-96': !isMobile,
        })}
      >
        <div className={clsx('flex', 'items-center', 'text-center')}>
          <Typography fontWeight="bold">{title}</Typography>
          <Link to={Routes.notificationList.path} className="ml-auto">
            <Button variant="text" onClick={seeAllCallback}>
              {t('notifications.popup.see-all')}
            </Button>
          </Link>
        </div>
        {!loading && records?.length > 0 && (
          <List className="p-0">
            {records.map((entity) => {
              const { name } = parseNotification(
                entity as Partial<NotificationPayload>,
                {
                  t,
                  currentUser: user!,
                },
              );
              return (
                <div id={entity.id} key={entity.id}>
                  <ListItem
                    className={clsx('grid', 'grid-cols-12', 'gap-3')}
                    key={entity.id}
                  >
                    <ListItemText className="flex-none">
                      {getCategoryIcon(entity.eventType?.enum)}
                    </ListItemText>
                    <ListItemText className="col-span-9">
                      <Typography variant="subtitle2">{name}</Typography>
                    </ListItemText>
                    <ListItemText className="col-span-2">
                      <Typography variant="subtitle2">
                        {formatDate(entity.createdAt, 'auto')}
                      </Typography>
                    </ListItemText>
                  </ListItem>
                  <Divider />
                </div>
              );
            })}
          </List>
        )}
        {!records?.length && (
          <div className={clsx('flex', 'gap-2', 'items-center')}>
            <Checked />
            <Typography variant="subtitle1">
              {t('notifications.popup.no-unread')}
            </Typography>
          </div>
        )}
      </ContentWrapper>
    </Can>
  );
};
